import { OfficeHelper } from "@/utils/helpers/office-helper";
import {
  faChevronDown,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { updateSelectedOfficeIds } from "@state/auth/AuthEvents";
import { authenticationStore } from "@state/auth/AuthStore";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { Checkbox } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useUnit } from "effector-react";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface HeaderOfficeDropdownProps {
  show: boolean;
  setShow: () => any;
  hideDropdown?: boolean;
  className?: string;
}

const HeaderOfficeDropdown: FunctionComponent<HeaderOfficeDropdownProps> = ({
  show,
  setShow,
  hideDropdown = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const checkedList = authStore?.selectedOfficeIds || [];

  const allAvailableOffices =
    authStore?.user?.offices.map((office) => ({
      label: office.alias,
      value: office.id,
    })) ?? [];

  const [checkAll, setCheckAll] = useState<boolean>(
    checkedList.length === authStore?.user?.offices.length,
  );

  useEffect(() => {
    setCheckAll(
      allAvailableOffices.length === authStore?.selectedOfficeIds?.length,
    );
  }, [allAvailableOffices, authStore?.selectedOfficeIds]);

  const onChange = (list: string[]) => {
    if (list.length >= 1) {
      const offices =
        authStore?.user?.offices.filter((office) => list.includes(office.id)) ??
        [];
      updateSelectedOfficeIds(
        OfficeHelper.mapOfficeToSelectedOfficesPayload(offices),
      );
    }
  };

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      const updatedCheckList = e.target.checked
        ? allAvailableOffices.map((office) => office.value)
        : [];
      const selectedOffices =
        authStore?.user?.offices.filter((office) =>
          updatedCheckList.includes(office.id),
        ) ?? [];
      updateSelectedOfficeIds(
        OfficeHelper.mapOfficeToSelectedOfficesPayload(selectedOffices),
      );
      return;
    } else {
      updateSelectedOfficeIds([]);
    }
    setCheckAll(e.target.checked);
  };

  const computeLabel = (): string => {
    const firstOffice = allAvailableOffices.find(
      (office) => office.value === checkedList[0],
    );
    const notAllCheckedLabel: string =
      checkedList.length > 1
        ? `${checkedList.length} ${t("header.selectOffices.selectedOffices")}`
        : ((firstOffice?.label as string) ??
          `0 ${t("header.selectOffices.selectedOffice")}`);
    return checkAll ? t("header.selectOffices.allOffices") : notAllCheckedLabel;
  };

  return (
    <div
      className={`app-dropdown-header dropdown-office ${className} ${hideDropdown ? "cursor-default mr-16" : ""}`}
      onClick={setShow}
    >
      <div className="dropdown-badge">{allAvailableOffices.length}</div>
      <div className="dropdown-icon">
        <img
          alt={t("header.office")}
          src="/img/core/header/icon-office.png"
          width={32}
          height={32}
        />
      </div>
      <div className="dropdown-text">{computeLabel()}</div>
      {!hideDropdown && (
        <div>
          <div className="dropdown-svg">
            <FontAwesomeIcon
              icon={faChevronDown}
              className="dropdown-chevron"
            />
          </div>
          <div
            className={`dropdown-container ${show ? "" : "hidden"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dropdown-message">
              {checkedList.length === 0 ? (
                <span className="text-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {t("header.selectOffices.noSelectCabinetMessage")}
                </span>
              ) : (
                <span>{t("header.selectOffices.selectCabinetMessage")}</span>
              )}
            </div>
            <Checkbox
              onChange={onCheckAllChange}
              checked={checkAll}
              className="checkbox-carded"
            >
              {allAvailableOffices.length ===
              authStore?.selectedOfficeIds?.length
                ? t("header.selectOffices.unselectAll")
                : t("header.selectOffices.selectAll")}
            </Checkbox>
            <Checkbox.Group
              options={allAvailableOffices}
              value={checkedList}
              onChange={onChange}
              className="checkbox-separated checkbox-carded"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderOfficeDropdown;
